/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  AmountDecimal,
  AmountDecimal$inboundSchema,
  AmountDecimal$Outbound,
  AmountDecimal$outboundSchema,
} from "./amountdecimal.js";

export type Residual = {
  /**
   * Unique identifier for this residual payment calculation.
   */
  residualID: string;
  /**
   * The partner account ID this residual belongs to.
   */
  partnerAccountID: string;
  /**
   * Start date and time of the residual calculation period.
   */
  periodStart: Date;
  /**
   * End date and time of the residual calculation period.
   */
  periodEnd: Date;
  /**
   * Total amount of merchant fees collected during the period. This represents the partner's revenue from merchant fees.
   */
  merchantFees: AmountDecimal;
  /**
   * Partner's total cost (buy rate) during the period.
   */
  partnerCost: AmountDecimal;
  /**
   * Net income calculated as merchant fee revenue minus partner costs.
   */
  netIncome: AmountDecimal;
  /**
   * The revenue share percentage the partner receives, expressed as a decimal string (e.g., "25.00" for 25%).
   */
  revenueShare: string;
  /**
   * The amount the partner receives as their share of the net income (netIncome × revenueShare).
   */
  residualAmount: AmountDecimal;
  /**
   * Timestamp when the residual was created.
   */
  createdOn: Date;
  /**
   * Timestamp when the residual was last updated.
   */
  updatedOn: Date;
};

/** @internal */
export const Residual$inboundSchema: z.ZodType<
  Residual,
  z.ZodTypeDef,
  unknown
> = z.object({
  residualID: z.string(),
  partnerAccountID: z.string(),
  periodStart: z.string().datetime({ offset: true }).transform(v =>
    new Date(v)
  ),
  periodEnd: z.string().datetime({ offset: true }).transform(v => new Date(v)),
  merchantFees: AmountDecimal$inboundSchema,
  partnerCost: AmountDecimal$inboundSchema,
  netIncome: AmountDecimal$inboundSchema,
  revenueShare: z.string(),
  residualAmount: AmountDecimal$inboundSchema,
  createdOn: z.string().datetime({ offset: true }).transform(v => new Date(v)),
  updatedOn: z.string().datetime({ offset: true }).transform(v => new Date(v)),
});
/** @internal */
export type Residual$Outbound = {
  residualID: string;
  partnerAccountID: string;
  periodStart: string;
  periodEnd: string;
  merchantFees: AmountDecimal$Outbound;
  partnerCost: AmountDecimal$Outbound;
  netIncome: AmountDecimal$Outbound;
  revenueShare: string;
  residualAmount: AmountDecimal$Outbound;
  createdOn: string;
  updatedOn: string;
};

/** @internal */
export const Residual$outboundSchema: z.ZodType<
  Residual$Outbound,
  z.ZodTypeDef,
  Residual
> = z.object({
  residualID: z.string(),
  partnerAccountID: z.string(),
  periodStart: z.date().transform(v => v.toISOString()),
  periodEnd: z.date().transform(v => v.toISOString()),
  merchantFees: AmountDecimal$outboundSchema,
  partnerCost: AmountDecimal$outboundSchema,
  netIncome: AmountDecimal$outboundSchema,
  revenueShare: z.string(),
  residualAmount: AmountDecimal$outboundSchema,
  createdOn: z.date().transform(v => v.toISOString()),
  updatedOn: z.date().transform(v => v.toISOString()),
});

export function residualToJSON(residual: Residual): string {
  return JSON.stringify(Residual$outboundSchema.parse(residual));
}
export function residualFromJSON(
  jsonString: string,
): SafeParseResult<Residual, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => Residual$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'Residual' from JSON`,
  );
}
